import { Media } from "types";

export enum CN_URL_PARTS {
  START_MULTIPART_UPLOAD = "startMultipartUpload",
  UPLOAD_CHUNK = "uploadChunk",
  FINISH_MULTIPART_UPLOAD = "finishMultipartUpload",
  CANCEL_MULTIPART_UPLOAD = "cancelUpload",
  DELETE_UPLOAD = "deleteUpload",
  UPLOAD_SINGLE_FILE = "upload",
}
export type ProgressCallback = {
  state: "start" | "progress" | "end";
  progress: number;
  total: number;
};
const MIME_TYPE_MAP: Record<string, string> = {
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  mp4: "video/mp4",
  mov: "video/quicktime",
  qt: "video/quicktime",
};
export const ALLOWED_COLS_DROP_ID = ["media"];

export const SUPPORTED_MEDIA_TYPES = [
  "image/png",
  "image/jpg",
  "image/jpeg",
  "video/mp4",
  "video/quicktime",
];

export const isMediaAllowedForId = (id: "media" | "mainImage") => {
  switch (id) {
    case "media":
      return {
        supportedTypes: [
          "image/png",
          "image/jpg",
          "image/jpeg",
          "video/mp4",
          "video/quicktime",
        ],
        maxFiles: 10,
        id: "media",
      };
    case "mainImage":
      return {
        supportedTypes: ["image/png", "image/jpg", "image/jpeg"],
        maxFiles: 1,
        id: "mainImage",
      };

    default:
      return {
        supportedTypes: [],
        maxFiles: 0,
        id: id,
      };
  }
};

/**
 * Get the sections from the path
 * @param path - The path to the file - Expects FULL PATH http://www.m.com/m/s/l/a/fileName.jpg
 * @returns {Object} An object containing the following properties:
 * @property {string} bucketP - The bucket name
 * @property {string} marketId - The market id
 * @property {string} saleId - The sale id
 * @property {string} lotId - The lot id
 * @property {string} attributeId - The attribute id
 * @property {string} fileName - The file name
 * @property {string} workingPath - The working path
 * @property {string} bucketUrl - The bucket url
 * @property {string} baseFileName - The base file name
 * @property {string} fileExtension - The file extension
 *
 */
export const getSectionsFromPath = (
  path: string
): {
  marketId: string;
  saleId: string;
  lotId: string;
  attributeId: string;
  fileName: string;
  workingPath: string;
  bucketUrl: string;
  baseFileName: string;
  fileExtension: string | undefined;
  cleanFileName: string;
  pathSuffix: string;
} | null => {
  const regex = /\/([^\/]+)/g;

  const matches = [...path.matchAll(regex)].map((match) => match[1]);

  if (matches.length < 4) {
    return null;
  }

  const [bucketP, marketId, saleId, lotId, attributeId, fileName] = matches;

  const workingPath = [marketId, saleId, lotId, attributeId].join("/");

  if (!fileName) {
    return null;
  }

  const bucketUrl = `https://${bucketP}`;

  const baseFileName = fileName?.split(".")[0];
  const fileExtension = fileName?.split(".").pop();
  const cleanFileName = baseFileName?.split("--")[0];
  const pathSuffix = baseFileName?.split("--")[1] as string;

  return {
    cleanFileName,
    fileExtension,
    baseFileName,
    workingPath,
    attributeId,
    pathSuffix,
    bucketUrl,
    fileName,
    marketId,
    saleId,
    lotId,
  };
};

// BASE URL OF THE WORKER - THAT IS WHERE THE FILES WILL BE UPLOADED
const BASE_CF_URL = "https://mediacrate.marteye.ie";
const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB - This is the default value as this is used SERVER TO SERVER WE CAN MAKE IT BUT HIGHER THAN IN APPS

export function getFilePath(
  marketId: string,
  saleId: string,
  lotId: string,
  attributeId: string,
  fileName: string
) {
  return `/${marketId}/${saleId}/${lotId}/${attributeId}/${fileName}`;
}

type CTX = {
  filePath: string;
  file: Blob;
  headers: Headers;
  chunkSize: number;
};

type Sections = {
  partNumber: string;
  eTag: string;
};

function getFileTotalParts(
  file: Blob,
  chunkSize: number
): {
  totalParts: number;
  chunkSize: number;
} {
  const totalParts = Math.ceil(file.size / chunkSize);
  return { totalParts, chunkSize };
}

/**
 * Upload a file to Cloudflare Worker using Multipart Upload
 * On Error it will try to cancel the upload
 * @param filePath - The path to the file
 * @param file - The file to upload
 * @param overRideChunkSize - The chunk size to use - Defaults to 5MB
 * @returns {Promise<{ok: true}>}
 */
export async function multiPartUploadFile({
  file,
  token,
  filePath,
  overRideChunkSize,
  progressCallback,
}: {
  filePath: string;
  token: string;
  file: Blob;
  overRideChunkSize?: number;
  progressCallback?: (d: ProgressCallback) => void;
}): Promise<{ ok: true }> {
  const headers = new Headers();
  headers.append("mbtype", "raw");
  headers.append("Authorization", `Bearer ${token}`);

  let uploadId: string = "";

  const ctx: CTX = {
    chunkSize: overRideChunkSize || CHUNK_SIZE,
    file,
    filePath,
    headers,
  };
  const mimeType = file.type;
  headers.append("mmimetype", mimeType);
  try {
    // Start Multipart Upload
    progressCallback &&
      progressCallback({ state: "start", progress: 0, total: 0 });
    uploadId = await startMultipartUpload(ctx);
    // Upload Parts
    const partsData = await uploadPart(ctx, uploadId, progressCallback);

    // Finalize
    const res = await completeMultipartUpload(ctx, uploadId, partsData).then(
      (res) => {
        progressCallback &&
          progressCallback({ state: "end", progress: 1, total: 1 });
        return res;
      }
    );
    return res;
  } catch (error) {
    console.error("Error", error);
    await cancelMultipartUpload(ctx, uploadId);
    throw new Error(error as unknown as any);
  }
}

async function startMultipartUpload(ctx: CTX): Promise<string> {
  const multiPartStartUploadUrl = new URL(
    `${BASE_CF_URL}/${CN_URL_PARTS.START_MULTIPART_UPLOAD}`
  );
  const formDataStart = new FormData();

  formDataStart.append("filePath", ctx.filePath);

  const uploadIdResponse = await fetch(multiPartStartUploadUrl, {
    method: "POST",
    headers: ctx.headers,
    body: formDataStart,
  });

  if (!uploadIdResponse.ok) {
    throw new Error("Failed to start Multipart Upload");
  }

  const multiPartUploadJson = (await uploadIdResponse.json()) as {
    data: { uploadId: string };
  };

  return multiPartUploadJson.data.uploadId;
}

async function uploadPart(
  ctx: CTX,
  uploadId: string,
  progressCallback?: (d: ProgressCallback) => void
): Promise<Sections[]> {
  const { totalParts } = getFileTotalParts(ctx.file, ctx.chunkSize);

  // UPLOAD PARTS
  const partsData = [] as Array<{ partNumber: string; eTag: string }>;
  const uploadPartUrl = new URL(`${BASE_CF_URL}/${CN_URL_PARTS.UPLOAD_CHUNK}`);
  for (let i = 0; i < totalParts; i++) {
    const start = ctx.chunkSize * i;
    const end = Math.min(ctx.file.size, start + ctx.chunkSize);
    const blob = ctx.file.slice(start, end);
    const partNumber = i + 1;

    const formData = new FormData();
    formData.append("file", blob);
    formData.append("filePath", ctx.filePath);
    formData.append("uploadId", uploadId);
    formData.append("partNumber", `${partNumber}`);
    progressCallback &&
      progressCallback({ state: "progress", progress: i, total: totalParts });
    const uploadPartResponse = await fetch(uploadPartUrl, {
      method: "POST",
      body: formData,
      headers: ctx.headers,
    });

    if (!uploadPartResponse.ok) {
      throw new Error("Failed to upload part");
    }

    const uploadPartJson = (await uploadPartResponse.json()) as {
      data: { eTag: string; partNumber: number };
    };
    const eTag = uploadPartJson.data.eTag;
    const partNumberResponse = uploadPartJson.data.partNumber;

    partsData.push({ partNumber: `${partNumberResponse}`, eTag });
  }
  return partsData;
}

async function completeMultipartUpload(
  ctx: CTX,
  uploadId: string,
  partsData: Sections[]
): Promise<{ ok: true }> {
  // FINALIZE
  const completeUploadUrl = new URL(
    `${BASE_CF_URL}/${CN_URL_PARTS.FINISH_MULTIPART_UPLOAD}`
  );

  const formDataDone = new FormData();

  formDataDone.append("uploadId", uploadId);
  formDataDone.append("filePath", ctx.filePath);
  formDataDone.append("sections", JSON.stringify(partsData));

  const completeUploadResponse = await fetch(completeUploadUrl, {
    method: "POST",
    body: formDataDone,
    headers: ctx.headers,
  });

  if (!completeUploadResponse.ok) {
    console.log("Error", completeUploadResponse);

    throw new Error("Failed to complete Multipart Upload");
  }

  return {
    ok: true,
  };
}

async function cancelMultipartUpload(
  ctx: CTX,
  uploadId: string
): Promise<void> {
  const abortUrl = new URL(
    `${BASE_CF_URL}/${CN_URL_PARTS.CANCEL_MULTIPART_UPLOAD}`
  );

  const formDataAbort = new FormData();
  formDataAbort.append("uploadId", uploadId);
  formDataAbort.append("filePath", ctx.filePath);

  const abortUploadResponse = await fetch(abortUrl, {
    headers: ctx.headers,
    method: "POST",
    body: formDataAbort,
  });
  console.log("AbortUploadResponse", abortUploadResponse);
}

export async function unlinkAssets(
  fileUrl: string,
  token: string,
  ignoreWrite?: boolean
) {
  const unlinkUrl = new URL(`${BASE_CF_URL}/${CN_URL_PARTS.DELETE_UPLOAD}`);
  const headers = new Headers();
  headers.append("Authorization", `Bearer ${token}`);
  ignoreWrite && headers.append("ignorewrite", "true");
  const formDataAbort = new FormData();
  formDataAbort.append("fileUrl", fileUrl);
  const unlinkAssets = await fetch(unlinkUrl, {
    headers: headers,
    method: "DELETE",
    body: formDataAbort,
  });

  if (!unlinkAssets.ok) {
    throw new Error("Error unlinking assets");
  }
  return unlinkAssets;
}

type UploadSingleFile =
  | {
      existingFilePath?: string;
      newFilePath: string;
      token: string;
      file?: never;
      ignoreWrite?: boolean;
    }
  | {
      newFilePath: string;
      file?: File;
      token: string;
      existingFilePath?: never;
      ignoreWrite?: boolean;
    };

export async function uploadSingleFile(params: UploadSingleFile) {
  const { existingFilePath, newFilePath, file, token, ignoreWrite } = params;
  const uploadUrl = new URL(
    `${BASE_CF_URL}/${CN_URL_PARTS.UPLOAD_SINGLE_FILE}`
  );

  let mimeType = file?.type;

  if (!mimeType && existingFilePath) {
    const extension = existingFilePath.split(".").pop()?.toLowerCase();
    mimeType = extension ? MIME_TYPE_MAP[extension] : undefined;
  }

  if (!mimeType) {
    throw new Error("No mime type found");
  }

  const headers = new Headers();
  headers.append("mbtype", "raw");
  headers.append("Authorization", `Bearer ${token}`);
  headers.append("mmimetype", mimeType);
  ignoreWrite && headers.append("ignorewrite", "true");

  const formData = new FormData();
  existingFilePath && formData.append("existingFilePath", existingFilePath);
  newFilePath && formData.append("filePath", newFilePath);
  file && formData.append("file", file);

  const uploadResponse = await fetch(uploadUrl, {
    headers: headers,
    method: "POST",
    body: formData,
  });

  if (!uploadResponse.ok) {
    throw new Error("Failed to upload single file");
  }

  const uploadResponseJson = await uploadResponse.json();

  return uploadResponseJson as {
    message: string;
    data: Media;
  };
}
